import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';
import { listProducts, deleteProduct } from '../../actions/productActions';
import { fetchCategories } from '../../actions/categoryActions';
import Loader from '../../components/ui/Loader';
import Message from '../../components/ui/Message';
import Paginate from '../../components/ui/Paginate';

const ProductsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get URL params
  const query = new URLSearchParams(location.search);
  const keywordParam = query.get('search') || '';
  const categoryParam = query.get('category') || '';
  const pageParam = parseInt(query.get('page') || '1', 10);

  const [keyword, setKeyword] = useState(keywordParam);
  const [category, setCategory] = useState(categoryParam);
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const dispatch = useDispatch();
  
  const productList = useSelector((state) => state.productList);
  const { loading, error, products, pagination } = productList;
  
  const categoryList = useSelector((state) => state.categoryList);
  const { categories } = categoryList;
  
  const productDelete = useSelector((state) => state.productDelete);
  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = productDelete;
  
  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(listProducts(keywordParam, categoryParam, '', pageParam));
    }, [dispatch, keywordParam, categoryParam, pageParam, successDelete]);

    // Update state when URL params change
    useEffect(() => {
      setKeyword(keywordParam);
      setCategory(categoryParam);
    }, [keywordParam, categoryParam]);

  
  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (keyword) params.append('search', keyword);
    if (category) params.append('category', category);
    params.append('page', '1');
    navigate(`/admin/products?${params.toString()}`);
  };
  
  const handleCategoryChange = (e) => {
    const newCategory = e.target.value;
    setCategory(newCategory);
    const params = new URLSearchParams();
    if (keyword) params.append('search', keyword);
    if (newCategory) params.append('category', newCategory);
    params.append('page', '1');
    navigate(`/admin/products?${params.toString()}`);
  };
  
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = () => {
    dispatch(deleteProduct(deleteId));
    setShowDeleteModal(false);
  };
  
  const cancelDelete = () => {
    setDeleteId(null);
    setShowDeleteModal(false);
  };
  
  return (
    <div className="admin-products-page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">产品管理</h1>
        <Link
          to="/admin/product"
          className="bg-green-600 text-white px-4 py-2 rounded flex items-center hover:bg-green-700"
        >
          <FaPlus className="mr-2" />
          添加产品
        </Link>
      </div>
      
      {loadingDelete && <Loader />}
      {errorDelete && <Message variant="error">{errorDelete}</Message>}
      {successDelete && <Message variant="success">产品已成功删除</Message>}
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="border-b bg-gray-50 px-6 py-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索产品..."
                  className="w-full p-2 pl-10 border rounded"
                />
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
              </div>
            </div>
            
            <div className="w-64">
              <select
                value={category}
                onChange={handleCategoryChange}
                className="w-full p-2 border rounded"
              >
                <option value="">所有分类</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              搜索
            </button>
          </form>
        </div>
        
        {loading ? (
          <div className="p-6">
            <Loader />
          </div>
        ) : error ? (
          <div className="p-6">
            <Message variant="error">{error}</Message>
          </div>
        ) : products.length === 0 ? (
          <div className="p-6 text-center">
            <Message>未找到产品</Message>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      ID
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      图片
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      名称
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      分类
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      成本价
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      价格
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      库存
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="w-12 h-12">
                          <img
                            src={product.image_url || '/images/default-wine.jpg'}
                            alt={product.name}
                            className="w-full h-full object-cover rounded"
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {product.name}
                        </div>
                        {product.chinese_name && (
                          <div className="text-sm text-gray-500">
                            {product.chinese_name}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.category_name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.cost_price ? `¥${parseFloat(product.cost_price).toFixed(2)}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ¥{parseFloat(product.price).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            product.stock_quantity > (product.min_stock_level || 10)
                              ? 'bg-green-100 text-green-800'
                              : product.stock_quantity > 0
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {product.stock_quantity > 0 ? (
                            <>
                              {product.stock_quantity}
                              {product.unit_per_box ? ` (${Math.floor(product.stock_quantity / product.unit_per_box)}箱)` : ''}
                            </>
                          ) : (
                            '缺货'
                          )}
                        </span>
                        {product.min_stock_level > 0 && (
                          <span className="ml-2 text-xs text-gray-500">
                            下限: {product.min_stock_level}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link
                          to={`/admin/product/${product.id}`}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          <FaEdit />
                        </Link>
                        <button
                          onClick={() => handleDeleteClick(product.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="px-6 py-4 border-t">
              {pagination && pagination.totalPages > 1 && (
                <Paginate
                  pages={pagination.totalPages}
                  page={pagination.page}
                  path="/admin/products"
                  extraParams={`${keywordParam ? `search=${keywordParam}&` : ''}${
                    categoryParam ? `category=${categoryParam}&` : ''
                  }`.replace(/&$/, '')}
                />
              )}
            </div>
          </>
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md mx-auto">
            <h3 className="text-xl font-bold mb-4">确认删除</h3>
            <p className="mb-6">您确定要删除此产品吗？此操作无法撤销。</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-100"
              >
                取消
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                {loadingDelete ? <Loader size="sm" /> : '删除'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;